import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { HistoricNarService } from "../../../../../api/services/historicNar/historicNarService";
import type { HistoricNarRecommendation } from "../../../../../api/services/historicNar/types";
import { logError } from "../../../../../utils/customLogger";

/** Load and refresh the complete Historic NAR recommendation set. */
const useHistoricNarRecommendations = () => {
  const hasStartedInitialFetch = useRef(false);
  const isMounted = useRef(false);
  const activeRequest = useRef<AbortController>();
  const [rows, setRows] = useState<HistoricNarRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchRecommendations = useCallback(
    async (options: { silent?: boolean } = {}) => {
      activeRequest.current?.abort();
      const controller = new AbortController();
      activeRequest.current = controller;
      setIsLoading(true);
      try {
        const recommendations = await HistoricNarService.listAllRecommendations(
          {},
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setRows(recommendations);
        if (recommendations.length === 0) {
          toast.error("No historic NAR recommendations found");
        } else if (!options.silent) {
          toast.success("Historic NAR recommendations loaded");
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        logError(error as Error, "Error fetching historic NAR recommendations");
        toast.error("Error loading historic NAR recommendations");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setHasFetched(true);
        }
        if (activeRequest.current === controller) {
          activeRequest.current = undefined;
        }
      }
    },
    [],
  );

  useEffect(() => {
    isMounted.current = true;
    // React Strict Mode replays mount effects; only start this request once.
    if (!hasStartedInitialFetch.current) {
      hasStartedInitialFetch.current = true;
      void fetchRecommendations();
    }

    return () => {
      isMounted.current = false;
      // Strict Mode immediately remounts effects in development. Deferring the
      // check preserves that request while still aborting a real unmount.
      void Promise.resolve().then(() => {
        if (!isMounted.current) activeRequest.current?.abort();
      });
    };
  }, [fetchRecommendations]);

  const refreshSilently = useCallback(
    () => fetchRecommendations({ silent: true }),
    [fetchRecommendations],
  );

  return {
    rows,
    isLoading,
    hasFetched,
    refresh: fetchRecommendations,
    refreshSilently,
  };
};

export default useHistoricNarRecommendations;
